// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { REORDER_PRODUCTS_ID } from "@keller/App/Widgets/ReorderPad/ReorderPad";
import { ProductCarouselProductStyles } from "@keller/Overrides/Components/ProductCarouselProduct";
import ProductListProductGridCard from "@keller/Overrides/Widgets/ProductList/ProductListProductGridCard";
import { xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    products: ProductModel[];
}

type Props = OwnProps;

interface ReorderPadGridStyles {
    container?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
}

const reorderPadGridStyles: ReorderPadGridStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            width: 100%;
            gap: 20px;

            ${xScreen("lte", "md")} {
                gap: 15px;
            }
        `,
    },
};

const styles = reorderPadGridStyles;

interface ReorderPadGridItemProps {
    product: ProductModel;
}

const mapStateToProps = (state: ApplicationState, ownProps: ReorderPadGridItemProps) => ({
    productInfo: getProductInfoFromList(state, REORDER_PRODUCTS_ID, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

const ReorderPadGridItem: FC<
    ReorderPadGridItemProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>
> = ({ product, productInfo, updateProductInfo }) => {
    if (!product || !productInfo) {
        return null;
    }

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <ProductListProductGridCard
                showCompare={true}
                showTitle={true}
                showAddToList={true}
                showBrand={true}
                showPrice={true}
                showPartNumbers={true}
                showAvailability={true}
                showImage={true}
                showAttributes={false}
                showReorderPadProductAttributes={true}
            />
        </ProductContext.Provider>
    );
};

const ProductGridItem = connect(mapStateToProps, mapDispatchToProps)(ReorderPadGridItem);

const ReorderPadGrid: FC<Props> = ({ products }) => {
    return (
        <StyledWrapper data-test-selector="reorderPadGridContainer" {...styles.container}>
            {products.map((product, idx) => (
                <ProductGridItem product={product} key={product.id} />
            ))}
        </StyledWrapper>
    );
};

export default ReorderPadGrid;
